import React from 'react';
import { Course, CourseStatus } from '@/types/course';
import { useImageUrl } from '@/services/uploads';
import { ClockIcon, UserGroupIcon, TrashIcon } from '@heroicons/react/24/outline';
import SettingsIcon from '../icons/SettingsIcon';
import HiddenIcon from '../icons/HiddenIcon';
import ActionMenu, { ActionMenuItem } from '../common/ActionMenu';
import PencilIcon from '@/components/icons/PencilIcon';

interface CourseCardProps {
  course: Course;
  onView?: (courseId: string) => void;
  onEdit?: (courseId: string) => void;
  onConfigure?: (courseId: string) => void;
  onHide?: (courseId: string) => void;
  onDelete?: (courseId: string) => void;
  'data-testid'?: string;
}

const CourseCard: React.FC<CourseCardProps> = ({
  course,
  onView,
  onEdit,
  onConfigure,
  onHide,
  onDelete,
  'data-testid': testId = 'course-card',
}) => {
  const [resolvedImageUrl] = useImageUrl(course.image_url);

  const formatDuration = (minutes?: number | null): string => {
    if (!minutes) return '--';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  // Mirrors ProgramStatusBadge so course and program cards read identically.
  const STATUS_CHIP: Record<string, { label: string; classes: string }> = {
    published: {
      label: 'Published',
      classes: 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400',
    },
    draft: {
      label: 'Draft',
      classes: 'bg-grey-100 text-grey-600 dark:bg-grey-800 dark:text-grey-400',
    },
    archived: {
      label: 'Hidden',
      classes: 'bg-error-50 text-error-700 dark:bg-error-900/30 dark:text-error-400',
    },
  };
  const statusChip = (status: CourseStatus) =>
    STATUS_CHIP[status] ?? { label: status, classes: STATUS_CHIP.draft.classes };

  const menuItems: ActionMenuItem[] = [
    {
      label: 'Edit',
      icon: <PencilIcon className="w-4 h-4" color="currentColor" accentColor={'rgb(var(--color-primary-100))'} />,
      onClick: () => onEdit?.(course.id),
      'data-testid': `${testId}-menu-edit`,
    },
    {
      label: 'Configure',
      icon: <SettingsIcon className="w-4 h-4" color="currentColor" accentColor={'rgb(var(--color-primary-100))'} />,
      onClick: () => onConfigure?.(course.id),
      'data-testid': `${testId}-menu-configure`,
    },
    {
      label: course.status === 'archived' ? 'Unhide' : 'Hide',
      // HiddenIcon defaults to grayscale; pass currentColor so it inherits the
      // menu's primary-tinted icon span (and the error tint when destructive).
      icon: <HiddenIcon className="w-4 h-4" color="currentColor" accentColor={'rgb(var(--color-primary-100))'} />,
      onClick: () => onHide?.(course.id),
      'data-testid': `${testId}-menu-hide`,
    },
    {
      label: 'Delete',
      icon: <TrashIcon className="w-4 h-4" />,
      onClick: () => onDelete?.(course.id),
      isDestructive: true,
      separatorAbove: true,
      'data-testid': `${testId}-menu-delete`,
    },
  ];

  return (
    <div
      data-testid={testId}
      className="relative flex flex-col w-full rounded-[28px] overflow-hidden border-[7px] border-[rgba(189,188,201,0.24)] bg-surface-primary shadow-[7px_7px_14px_0px_rgba(0,0,0,0.2)] group"
    >
      {/* Cover banner — a short image strip, badges overlaid */}
      <div className="relative h-[150px] shrink-0 overflow-hidden">
        {resolvedImageUrl ? (
          <img
            src={resolvedImageUrl}
            alt={course.title}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`${testId}-image`}
          />
        ) : (
          <div
            className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-700"
            data-testid={`${testId}-placeholder`}
          />
        )}

        {/* Duration + Learners badges over the image */}
        <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3">
          <div className="relative flex items-center gap-1.5 px-1.5 py-0.5 rounded-[6px] overflow-hidden">
            <div className="absolute inset-0 bg-white/75 rounded-[6px]" />
            <ClockIcon className="relative w-4 h-4 text-grey-900" />
            <span className="relative text-xs font-semibold text-grey-900">
              {formatDuration(course.estimated_duration_minutes)}
            </span>
          </div>

          <div className="relative flex items-center gap-1 px-2 py-0.5 rounded-[6px] overflow-hidden">
            <div className="absolute inset-0 bg-black/50 rounded-[6px]" />
            <UserGroupIcon className="relative w-4 h-4 text-white" />
            <span className="relative text-[13px] font-extrabold text-white">
              {course.enrolled_learner_count ?? 0} Learner{(course.enrolled_learner_count ?? 0) === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      </div>

      {/* Footer — solid, theme-aware */}
      <div className="flex flex-col gap-2 p-4">
        <h3
          className="text-base font-extrabold text-text-primary leading-tight line-clamp-2 min-h-[2.5em]"
          data-testid={`${testId}-title`}
        >
          {course.title}
        </h3>

        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${statusChip(course.status).classes}`}
          >
            {statusChip(course.status).label}
          </span>
          <span className="text-[13px] text-text-tertiary">Updated {formatDate(course.updated_at)}</span>
        </div>

        <div className="flex items-center gap-2 mt-1">
          <button
            onClick={() => onView?.(course.id)}
            className="flex-1 py-1.5 px-2 text-[13px] font-bold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 transition-[background-color] duration-150"
            data-testid={`${testId}-view-btn`}
          >
            View Course
          </button>

          <ActionMenu
            items={menuItems}
            placement="top"
            align="right"
            data-testid={`${testId}-menu`}
          >
            {({ open, toggle }) => (
              <button
                type="button"
                onClick={toggle}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Course actions"
                className="flex items-center justify-center w-8 h-8 shrink-0 bg-neutral-100 dark:bg-neutral-800 border border-border-primary/20 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-[background-color] duration-150"
                data-testid={`${testId}-menu-btn`}
              >
                <SettingsIcon className="w-4 h-4 text-text-secondary" accentColor="transparent" />
              </button>
            )}
          </ActionMenu>
        </div>
      </div>
    </div>
  );
};

export default CourseCard;
